import type { Discovery } from "@/domain/types";

const permitBrand: unique symbol = Symbol("Discovery revision intent");
export type DiscoveryRevisionPermit = { readonly [permitBrand]: true; readonly id: string };
type Binding = Pick<Discovery, "id" | "sessionId" | "modelId">;
const permits = new WeakMap<
  DiscoveryRevisionPermit,
  { customerId: string; binding: Binding; origin: "improve" | "resume"; opened: boolean }
>();

function issue(
  customerId: string,
  discovery: Binding,
  id: string,
  origin: "improve" | "resume",
): DiscoveryRevisionPermit {
  const permit: DiscoveryRevisionPermit = Object.freeze({ [permitBrand]: true as const, id });
  permits.set(permit, {
    customerId,
    origin,
    opened: origin === "resume",
    binding: {
      id: discovery.id,
      sessionId: discovery.sessionId,
      ...(discovery.modelId ? { modelId: discovery.modelId } : {}),
    },
  });
  return permit;
}

/** The intentional Improve action is the only entry point that creates a new revision. */
export function improveEstimate(
  customerId: string,
  discovery: Discovery,
  begin: (permit: DiscoveryRevisionPermit) => Promise<void>,
): Promise<void> {
  if (!discovery.result || discovery.stage !== "result") return Promise.resolve();
  return begin(issue(customerId, discovery, crypto.randomUUID(), "improve"));
}

/** Restore only an already persisted revision marker when reading a validated session. */
export function resumeEstimateRevision(
  customerId: string,
  discovery: Discovery,
  revisionId: string,
) {
  return issue(customerId, discovery, revisionId, "resume");
}

export function permitsEstimateRevision(
  permit: DiscoveryRevisionPermit | undefined,
  customerId: string,
  discovery: Binding,
  starting = false,
): permit is DiscoveryRevisionPermit {
  const saved = permit && permits.get(permit);
  return Boolean(
    saved &&
    (!starting || (saved.origin === "improve" && !saved.opened)) &&
    saved.customerId === customerId &&
    saved.binding.id === discovery.id &&
    saved.binding.sessionId === discovery.sessionId &&
    saved.binding.modelId === discovery.modelId,
  );
}

/** Consume the fresh entry permission after the revision transaction succeeds. */
export function consumeEstimateRevisionIntent(permit: DiscoveryRevisionPermit) {
  const saved = permits.get(permit);
  if (saved) saved.opened = true;
}
